import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { CheckCircle2, KeyRound, ShieldAlert } from 'lucide-react';
import { BrandMark } from '@/layout/BrandMark';
import { Button } from '@/components/ui/Button';
import { isWebAuthnSupported, startRegistration } from './webauthn';

/**
 * Landing page for one-time passkey registration links. The token is part of
 * the URL; if it's valid the user can create a platform authenticator and is
 * redirected to the sign-in page on success.
 */
export function RegisterPasskeyPage() {
  const { token = '' } = useParams<{ token?: string }>();
  const navigate = useNavigate();
  const [supported] = useState<boolean>(() => isWebAuthnSupported());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const handleRegister = async () => {
    setBusy(true);
    setError(null);
    try {
      await startRegistration(token);
      setDone(true);
      setTimeout(() => navigate('/login', { replace: true }), 2500);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Registration failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4 py-10 dark:bg-gray-900">
      <div className="w-full max-w-md rounded-2xl border border-gray-200 bg-white p-8 shadow-card dark:border-gray-800 dark:bg-gray-950 dark:shadow-card-dark">
        <div className="mb-8 flex items-center justify-center">
          <BrandMark showWordmark />
        </div>

        <h1 className="text-center text-2xl font-semibold tracking-tight text-gray-900 dark:text-white">
          {done ? 'Passkey created' : 'Create a passkey'}
        </h1>
        <p className="mt-2 text-center text-sm text-gray-500 dark:text-gray-400">
          {done
            ? 'Your passkey is ready. Redirecting you to sign in…'
            : 'Follow the prompt from your browser to save a passkey for this device.'}
        </p>

        <div className="mt-8 flex flex-col items-center gap-4 rounded-xl border border-dashed border-gray-300 bg-gray-50 p-8 text-center dark:border-gray-700 dark:bg-gray-900">
          {done ? (
            <CheckCircle2
              className="h-12 w-12 text-emerald-500"
              aria-hidden="true"
            />
          ) : (
            <KeyRound className="h-12 w-12 text-brand-500" aria-hidden="true" />
          )}
          {!done && (
            <Button
              id="admin.register.submit"
              onClick={handleRegister}
              loading={busy}
              disabled={!supported || token === ''}
              size="lg"
              fullWidth
            >
              {busy ? 'Waiting for passkey…' : 'Create passkey'}
            </Button>
          )}
          {!supported && (
            <p className="flex items-start gap-2 text-sm text-amber-700 dark:text-amber-300">
              <ShieldAlert
                className="mt-0.5 h-4 w-4 shrink-0"
                aria-hidden="true"
              />
              This browser does not support passkeys. Please use Safari,
              Chrome, Edge or Firefox on a device with biometrics.
            </p>
          )}
          {error && (
            <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
          )}
        </div>

        <p className="mt-6 text-center text-xs text-gray-500 dark:text-gray-400">
          Registration links expire after one hour and can only be used once.
        </p>
      </div>
    </div>
  );
}
